/**
 * Audio Stream Manager - High-level API for audio capture and streaming
 * Integrates Web Audio API, audio processing, and WebSocket transport
 */

/* eslint-disable @typescript-eslint/no-parameter-properties */

import { AudioProcessor } from '../core/AudioProcessor'
import { AudioQualityEnhancer } from '../core/AudioQualityEnhancer'
import { WebSocketClient } from './WebSocketClient'
import { StreamConfig } from '../core/types'

/**
 * AudioStreamManager - Complete audio streaming solution
 *
 * This class provides the high-level API for:
 * - Microphone capture via Web Audio API
 * - Audio quality enhancement pipeline
 * - Real-time WebSocket streaming
 * - Resource cleanup and lifecycle management
 *
 * Usage:
 * ```typescript
 * const manager = new AudioStreamManager(wsClient, config);
 * await manager.startCapture();
 * // ... recording happens ...
 * manager.stopCapture();
 * ```
 */
export class AudioStreamManager {
  private audioContext: AudioContext | null = null
  private processor: ScriptProcessorNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private stream: MediaStream | null = null
  private audioProcessor: AudioProcessor
  private wsClient: WebSocketClient

  constructor(wsClient: WebSocketClient, private config: StreamConfig) {
    this.wsClient = wsClient

    // Initialize audio processor
    this.audioProcessor = new AudioProcessor({
      sampleRate: config.sampleRate,
      bufferSize: config.bufferSize,
      enableQualityEnhancement: config.enableQualityEnhancement,
      processors: []
    })

    // Add quality enhancement if enabled
    if (config.enableQualityEnhancement && config.qualityConfig) {
      const enhancer = new AudioQualityEnhancer(
        config.qualityConfig,
        config.sampleRate
      )
      this.audioProcessor.addProcessor(enhancer)
    }
  }

  /**
   * Start audio capture and streaming
   * @throws Error if microphone access denied or WebSocket connection fails
   */
  async startCapture(): Promise<void> {
    // Request microphone access
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: this.config.sampleRate,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false  // We handle AGC in our pipeline
      },
      video: false
    })

    // Create Audio Context (webkit fallback for older browsers)
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const audioContextConstructor =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      window.AudioContext || (window as any).webkitAudioContext
    this.audioContext = new audioContextConstructor({
      sampleRate: this.config.sampleRate
    })

    // Create audio processing nodes
    this.source = this.audioContext.createMediaStreamSource(this.stream)
    this.processor = this.audioContext.createScriptProcessor(
      this.config.bufferSize,
      1, // mono input
      1  // mono output
    )

    // Audio processing callback
    this.processor.onaudioprocess = (event) => {
      if (this.wsClient.isWebSocketConnected()) {
        const inputBuffer = event.inputBuffer.getChannelData(0)

        // Process audio through quality enhancement pipeline
        const processedBuffer = this.audioProcessor.processBuffer(inputBuffer)

        // Send to WebSocket (cast to ArrayBuffer - Int16Array.buffer is always ArrayBuffer, not SharedArrayBuffer)
        this.wsClient.sendAudioData(processedBuffer.buffer as ArrayBuffer)
      }
    }

    // Connect audio graph
    this.source.connect(this.processor)
    this.processor.connect(this.audioContext.destination)

    // Start WebSocket streaming
    this.wsClient.startStreaming()
  }

  /**
   * Stop audio capture and cleanup resources
   */
  stopCapture(): void {
    // Disconnect audio nodes
    if (this.processor) {
      this.processor.disconnect()
      this.processor = null
    }

    if (this.source) {
      this.source.disconnect()
      this.source = null
    }

    // Close audio context
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    // Stop media tracks
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }

    // End WebSocket streaming
    this.wsClient.endStreaming()

    // Reset audio processor state
    this.audioProcessor.reset()
  }

  /**
   * Check if currently capturing audio
   */
  isCapturing(): boolean {
    return this.audioContext !== null && this.stream !== null
  }

  /**
   * Get active audio processor names
   */
  getActiveProcessors(): string[] {
    return this.audioProcessor.getProcessorNames()
  }

  /**
   * Get current configuration
   */
  getConfig(): StreamConfig {
    return { ...this.config }
  }
}
